// ROOT: frontend/src/components/TiktokPricing.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TIKTOK_PLANS } from '../mock';
import {
  Check,
  ChevronRight,
  Music2,
  Users,
  TrendingUp,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { api } from '../api';

const TT_TRUST = [
  { icon: Users, text: 'Trending with 12K+ TikTokers this week' },
  { icon: TrendingUp, text: '2,140 fans delivered in the last hour' },
  { icon: ShieldCheck, text: '100% money-back guarantee' },
  { icon: Zap, text: 'Setup in under 2 minutes' },
];

const TrustStrip = ({ offset = 0 }) => {
  const [idx, setIdx] = useState(offset % TT_TRUST.length);

  useEffect(() => {
    const id = setInterval(
      () => setIdx((v) => (v + 1) % TT_TRUST.length),
      3200
    );

    return () => clearInterval(id);
  }, []);

  const { icon: Icon, text } = TT_TRUST[idx];

  return (
    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-[11px] font-semibold text-slate-600">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70"></span>
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
      </span>

      <Icon className="w-3 h-3 text-slate-400 shrink-0" />

      <span key={idx} className="fade-in-up truncate">
        {text}
      </span>
    </div>
  );
};

const TiktokPricing = () => {
  const [billing, setBilling] = useState('yearly');
  const [brandName, setBrandName] = useState('IGGrowth');
  const [pricing, setPricing] = useState(TIKTOK_PLANS);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await api.getPublicSettings();

        const name =
          settings?.brandName ||
          settings?.brand_name ||
          'IGGrowth';

        setBrandName(name);

        /*
         * Admin pricing format:
         *
         * Monthly:
         * Grow: 89
         * Scale: 149
         * Elite: 249
         *
         * Yearly:
         * Grow: 44
         * Scale: 74
         * Elite: 124
         *
         * If only one section is provided, the other section
         * continues using the default values from mock.js.
         */

        const pricingText =
          settings?.tiktokPricing ||
          settings?.tiktok_pricing ||
          '';

        if (pricingText.trim()) {
          const sections = {
            monthly: {},
            yearly: {},
          };

          let currentSection = 'monthly';

          pricingText.split('\n').forEach((line) => {
            const trimmed = line.trim();

            if (!trimmed) return;

            const lower = trimmed.toLowerCase();

            if (lower === 'monthly:' || lower === 'monthly') {
              currentSection = 'monthly';
              return;
            }

            if (lower === 'yearly:' || lower === 'yearly') {
              currentSection = 'yearly';
              return;
            }

            const parts = trimmed.split(':');

            if (parts.length >= 2) {
              const planName = parts[0].trim().toLowerCase();

              const price = Number(
                parts
                  .slice(1)
                  .join(':')
                  .replace(/[^0-9.]/g, '')
              );

              if (
                planName &&
                Number.isFinite(price) &&
                price > 0
              ) {
                sections[currentSection][planName] = price;
              }
            }
          });

          setPricing((currentPricing) => ({
            monthly: currentPricing.monthly.map((plan) => {
              const adminPrice =
                sections.monthly[plan.name.toLowerCase()];

              return adminPrice
                ? {
                    ...plan,
                    price: adminPrice,
                  }
                : plan;
            }),

            yearly: currentPricing.yearly.map((plan) => {
              const adminPrice =
                sections.yearly[plan.name.toLowerCase()];

              return adminPrice
                ? {
                    ...plan,
                    price: adminPrice,
                  }
                : plan;
            }),
          }));
        }
      } catch (error) {
        console.error('Failed to load TikTok pricing settings:', error);
      }
    };

    loadSettings();
  }, []);

  const plans = pricing[billing];

  return (
    <section id="tiktok" className="py-24 bg-slate-50/50">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">

        <div className="text-center max-w-3xl mx-auto mb-10">

          <p className="text-sm font-bold tracking-widest uppercase plixy-gradient-text inline-flex items-center gap-2 justify-center">
            <Music2 className="w-4 h-4" /> TikTok Growth
          </p>

          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#0a1930]">
            {brandName} TikTok Growth{' '}
            <span className="plixy-gradient-text">
              Pricing &amp; Plans
            </span>
          </h2>

          <p className="mt-4 text-lg text-slate-600">
            Your own TikTok team. Guaranteed Results or It's Free!
            <br />
            200+ hours saved every month. Get up to 400% more real, engaged fans.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center p-1.5 rounded-full bg-white border border-slate-200 shadow-sm">

            <button
              onClick={() => setBilling('yearly')}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all inline-flex items-center gap-2 ${
                billing === 'yearly'
                  ? 'bg-white text-[#0a1930] shadow-md'
                  : 'text-slate-500 hover:text-[#0a1930]'
              }`}
            >
              Yearly

              <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                save 50%
              </span>
            </button>

            <button
              onClick={() => setBilling('monthly')}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                billing === 'monthly'
                  ? 'bg-[#0a1930] text-white shadow-md'
                  : 'text-slate-500 hover:text-[#0a1930]'
              }`}
            >
              Monthly
            </button>

          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">

          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-3xl p-7 bg-white border ${
                plan.highlight
                  ? 'border-transparent shadow-2xl md:scale-105'
                  : 'border-slate-200'
              } transition-all`}
            >

              {plan.highlight && (
                <>
                  <div className="absolute inset-0 rounded-3xl plixy-gradient-ring opacity-10 pointer-events-none" />

                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold text-white plixy-gradient-ring">
                    BEST VALUE
                  </div>
                </>
              )}

              <h3 className="text-xl font-extrabold text-[#0a1930]">
                {plan.name}
              </h3>

              <p className="mt-1 text-xs font-semibold text-slate-500">
                {plan.range}
              </p>

              <div className="mt-5 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-[#0a1930]">
                  ${plan.price}
                </span>

                <span className="text-slate-500 font-semibold text-sm">
                  /mo
                </span>
              </div>

              {billing === 'yearly' && (
                <p className="text-xs text-emerald-600 font-bold mt-1">
                  Billed annually · saves 50%
                </p>
              )}

              <Link
                to={`/get-started?plan=tiktok-${plan.name.toLowerCase()}&platform=tiktok&price=${plan.price}&name=${encodeURIComponent(
                  'TikTok ' + plan.name
                )}&range=${encodeURIComponent(
                  plan.range
                )}&billing=${billing}`}
                className="mt-5 w-full inline-flex items-center justify-center gap-1 py-3 rounded-full font-semibold text-sm plixy-dark-btn"
              >
                Get Started
                <ChevronRight className="w-4 h-4" />
              </Link>

              <ul className="mt-5 space-y-2.5">

                {plan.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-sm"
                  >
                    <span className="w-4 h-4 shrink-0 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                      <Check
                        className="w-2.5 h-2.5 text-emerald-600"
                        strokeWidth={3}
                      />
                    </span>

                    <span className="font-semibold text-[#0a1930]">
                      {f}
                    </span>
                  </li>
                ))}

              </ul>

              <TrustStrip offset={plan.highlight ? 1 : 0} />

            </div>
          ))}

        </div>
      </div>
    </section>
  );
};

export default TiktokPricing;
